import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';

export type MegaMenuCustomNavSlot = 1 | 2 | 3;

export const MEGA_MENU_CUSTOM_NAV_SLOTS: MegaMenuCustomNavSlot[] = [1, 2, 3];

export const CUSTOM_MEGA_MENU_NAV_REGION_IDS: Record<MegaMenuCustomNavSlot, string> = {
    1: 'custom_megamenu_1_nav',
    2: 'custom_megamenu_2_nav',
    3: 'custom_megamenu_3_nav',
};

export const CUSTOM_MEGA_MENU_COLUMN_REGION_IDS: Record<MegaMenuCustomNavSlot, readonly string[]> = {
    1: [
        'custom_megamenu_1_column_1',
        'custom_megamenu_1_column_2',
        'custom_megamenu_1_column_3',
        'custom_megamenu_1_column_4',
    ],
    2: [
        'custom_megamenu_2_column_1',
        'custom_megamenu_2_column_2',
        'custom_megamenu_2_column_3',
        'custom_megamenu_2_column_4',
    ],
    3: [
        'custom_megamenu_3_column_1',
        'custom_megamenu_3_column_2',
        'custom_megamenu_3_column_3',
        'custom_megamenu_3_column_4',
    ],
};

export type MegaMenuCustomNavItem = {
    id: string;
    slot: MegaMenuCustomNavSlot;
    label: string;
    to: string;
    /** True when at least one dropdown column region for this slot has content. */
    hasDropdownContent: boolean;
};

function getDataRecord(data: unknown): Record<string, unknown> {
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

function endsWithTypeId(typeId: string, shortId: string): boolean {
    return typeId === shortId || typeId.endsWith(`.${shortId}`);
}

export function isMegaMenuCustomNavLinkComponent(typeId: string): boolean {
    return endsWithTypeId(typeId, 'megaMenuCustomNavLink');
}

export function getMegaMenuCustomNavItemId(slot: MegaMenuCustomNavSlot): string {
    return `custom-megamenu-${slot}`;
}

export function customMegaMenuSlotHasDropdownContent(
    resolved: ComponentWithComponentData | null | undefined,
    slot: MegaMenuCustomNavSlot
): boolean {
    return CUSTOM_MEGA_MENU_COLUMN_REGION_IDS[slot].some(
        (regionId) => (resolved?.regions?.find((region) => region.id === regionId)?.components?.length ?? 0) > 0
    );
}

export function parseMegaMenuCustomNavItems(
    resolved: ComponentWithComponentData | null | undefined
): MegaMenuCustomNavItem[] {
    const items: MegaMenuCustomNavItem[] = [];

    for (const slot of MEGA_MENU_CUSTOM_NAV_SLOTS) {
        const navRegionId = CUSTOM_MEGA_MENU_NAV_REGION_IDS[slot];
        const region = resolved?.regions?.find((entry) => entry.id === navRegionId);
        const component = region?.components?.[0];

        if (!component || !isMegaMenuCustomNavLinkComponent(component.typeId)) {
            continue;
        }

        const data = getDataRecord(component.data);
        const label = typeof data.label === 'string' ? data.label.trim() : '';
        if (!label) {
            continue;
        }

        const to = typeof data.to === 'string' && data.to.trim() ? data.to.trim() : '/';

        items.push({
            id: getMegaMenuCustomNavItemId(slot),
            slot,
            label,
            to,
            hasDropdownContent: customMegaMenuSlotHasDropdownContent(resolved, slot),
        });
    }

    return items;
}
